import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTabsModule } from '@angular/material/tabs';
import { Student, StudentFilterParams, StudentItem } from '@edtech-ui/core/models/admin-panel/admin-panel-models';
import { StudentManagementService } from '@edtech-ui/core/services/admin-panel/studentManagement.service';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { BulkUploadDialogComponent } from '../bulk-upload-dialog/bulk-upload-dialog.component';
import { StudentInviteDialogComponent } from '../student-invite-dialog/student-invite-dialog.component';
import { CreateBatchDialogComponent } from '../create-batch-dialog/create-batch-dialog.component';
import { TeacherManagementService } from '@edtech-ui/core/services/admin-panel/teacherManagement.service';
import { ToastrService } from 'ngx-toastr';
import { BatchManagementService } from '@edtech-ui/core/services/batch-management/batch-management.service';
import { PaginatorModule } from 'primeng/paginator';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { AdminHeaderComponent } from '../admin-header/admin-header.component';
import { debounceTime, distinctUntilChanged, Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-student-management-landing',
  standalone: true,
  imports: [
    CommonModule,
    MatTabsModule,
    MatButtonModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
    MatFormFieldModule,
    TableModule,
    TagModule,
    ButtonModule,
    InputTextModule,
    FormsModule,
    PaginatorModule,
    AdminHeaderComponent
  ],
  templateUrl: './student-management-landing.component.html',
  styleUrl: './student-management-landing.component.scss'
})
export class StudentManagementLandingComponent implements OnInit {
  private destroy$ = new Subject<void>();
  adminEmail = '';
  searchText: string = '';
  activeTabIndex = 0;
  isLoading = false;


  departmentId: string | number = 'All';
  batchId: string | number = 'All';

  departments: { id: string | number; name: string }[] = [];
  batches: { id: number | string; name: string }[] = [];

  students: StudentItem[] = [];
  page = 1;
  limit = 10;
  totalRecords = 0;

  pendingInvites: StudentItem[] = [];
  departmentSummary: any[] = [];
  tenantName = 'Student Management';
  tenantSubName = 'Manage students and enrollments';


  constructor(private studentService: StudentManagementService, private dialog: MatDialog,
    private teacherService: TeacherManagementService,
    private toastr: ToastrService,
    private batchService: BatchManagementService) { }
  private searchSubject = new Subject<string>();



  ngOnInit() {
    this.loadDepartments();
    this.loadBatches();
    this.loadDataBasedOnTab();
    this.searchSubject
      .pipe(
        debounceTime(300),
        distinctUntilChanged(), takeUntil(this.destroy$)
      )
      .subscribe(search => {
        this.page = 1;

        if (search.trim().length > 0) {
          this.loadStudentsBasedOnFilter(search);
          return;
        }

        this.loadStudentsBasedOnFilter('');
      });

  }
  loadDepartments() {
    this.teacherService.getDepartments().pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        if (res.Success && res.Data) {
          this.departments = [
            { id: 'All', name: 'All Departments' },
            ...res.Data.map((d: any) => ({
              id: d.id,
              name: d.name
            }))
          ];
          this.departmentId = 'All';
        }
      },
      error: () => {
        this.toastr.error('Failed to load departments');
      }
    });
  }

  loadBatches() {
    this.batchService.getAllTenantsBatches().pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        if (res.Success && res.Data?.batches) {
          this.batches = [
            { id: 'All', name: 'All Batches' },
            ...res.Data.batches.map((b: any) => ({
              id: b.id,
              name: b.name
            }))
          ];

          this.batchId = 'All';
        }
      },
      error: () => {
        this.toastr.error('Failed to load batches');
      }
    });
  }
  onTabChange(index: number) {
    this.activeTabIndex = index;
    this.loadDataBasedOnTab();
  }

  loadDataBasedOnTab() {
    if (this.activeTabIndex === 0) {
      this.loadStudents('active');
    }
    else if (this.activeTabIndex === 1) {
      this.loadStudents('pending_invites');
    }
    else if (this.activeTabIndex === 2) {
      this.loadStudents('by_batch');
    }
  }

  loadStudents(filterType: 'active' | 'pending_invites' | 'by_batch', search: string = '') {

    const params: StudentFilterParams = {
      filter_type: filterType,
      page: this.page,
      limit: this.limit,
      department_id: this.departmentId,
      batch_id: this.batchId,
      search: search
    };

    this.studentService.getFilteredStudents(params).pipe(takeUntil(this.destroy$)).subscribe(res => {
      if (!res.Success) return;

      if (filterType === 'active') {
        this.students = res.Data.students || [];
        this.totalRecords = res.Data.total_count ?? 0;
      }
      else if (filterType === 'pending_invites') {
        this.pendingInvites = res.Data.invites || [];
        this.totalRecords = res.Data.total_count ?? this.pendingInvites.length;
      }
      else if (filterType === 'by_batch') {
        this.departmentSummary = (res.Data as any) || [];
      }
    });
  }

  loadStudentsBasedOnFilter(search: string = '') {
    const filter =
      this.activeTabIndex === 0 ? 'active' :
        this.activeTabIndex === 1 ? 'pending_invites' :
          'by_batch';

    this.loadStudents(filter, search);
  }


onPageChange(event: any) {
  this.page = event.page + 1;
  this.loadDataBasedOnTab();
}


  onFilterChange() {
    this.page = 1;
    this.loadDataBasedOnTab();
  }

  onBulkUpload() {
    this.dialog.open(BulkUploadDialogComponent, {
      data: { type: 'student' },
      width: '520px',
      maxWidth: '95vw',
      panelClass: 'custom-dialog-container',
      disableClose: true,
    });
  }

  onInviteStudent() {
    this.dialog.open(StudentInviteDialogComponent, {
      panelClass: 'custom-dialog-container',
      disableClose: true
    });
  }

  onCreateBatch() {
    this.dialog.open(CreateBatchDialogComponent, {
      width: '500px',
      maxWidth: '95vw',
      panelClass: 'custom-dialog-container',
      disableClose: true
    });
  }

  deleteStudent(student: any) {
    const isPending = this.activeTabIndex === 1;

    const identifier = isPending
      ? student.invite_id || student.user_id
      : student.user_id;

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '32rem',
      maxWidth: '95vw',
      panelClass: 'custom-dialog-container',
      data: {
        title: isPending ? 'Revoke Invitation?' : 'Delete Student?',
        message: isPending
          ? `This will permanently revoke the invitation for ${student.name}.`
          : `This will permanently delete ${student.name}. This action cannot be undone.`,
        confirmText: isPending ? 'Revoke' : 'Delete',
        cancelText: 'Cancel'
      }
    });

    dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((confirmed) => {
      if (!confirmed || !identifier) return;

      const deleteCall = isPending
        ? this.teacherService.deletePendingInvite(identifier.toString())
        : this.teacherService.deleteActiveUser(identifier.toString());

      deleteCall.pipe(takeUntil(this.destroy$)).subscribe({
        next: (res) => {
          if (res.Success) {
            this.toastr.success(
              isPending
                ? `Invitation for ${student.name} revoked.`
                : `${student.name} deleted successfully.`
            );
            this.loadDataBasedOnTab();
          } else {
            this.toastr.warning(res.Message || 'Operation failed.');
          }
        },
        error: () => {
          this.toastr.error('Something went wrong while deleting.');
        }
      });
    });
  }
  searchStudents(query: string) {
    this.searchSubject.next(query);
  }
  // executeSearch(query: string) {
  //   if (!query || query.trim().length === 0) {
  //     this.loadDataBasedOnTab();
  //     return;
  //   }

  //   this.isLoading = true;

  //   this.studentService.searchStudents(query).subscribe({
  //     next: (res) => {
  //       this.isLoading = false;

  //       const students = res.Data?.students || [];

  //       if (res.Success && students.length > 0) {
  //         const mapped: StudentItem[] = students.map((s: any) => ({
  //           user_id: s.user_id,
  //           name: s.name || '—',
  //           email: s.email || '—',
  //           batch: s.batch_name || '—',
  //           department: s.department_name || '—',
  //           status: s.status || 'Active',
  //           join_date: s.join_date || s.invited_on || null,
  //           joined: s.join_date
  //             ? new Date(s.join_date).toLocaleDateString('en-IN', {
  //               year: 'numeric',
  //               month: 'short',
  //               day: 'numeric',
  //             })
  //             : '—',
  //           invited_on: s.invited_on || '—',
  //         }));

  //         if (this.activeTabIndex === 0) this.students = mapped;
  //         else if (this.activeTabIndex === 1) this.pendingInvites = mapped;

  //       } else {
  //         this.toastr.info('No students found for this search.');
  //       }
  //     },

  //     error: () => {
  //       this.isLoading = false;
  //       this.toastr.error('Error while searching students.');
  //     }
  //   });
  // }
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
